/**
 * Background photos for the featured match on Home. The files live in
 * `public/heroes/` (JPG, about 1600 wide; the card darkens the left side for
 * the text). Order: women's matches get the women's photo, then a
 * competition's own photo, then one of the default photos (always the same
 * one for a competition, so a league doesn't change look between visits).
 */

/** League heading (as the relay groups it, any case) → file in public/heroes/. Add a line when a photo is added. */
export const HERO_FILES: Record<string, string> = {
  'Premier League': 'premier-league.jpg',
  Championship: 'championship.jpg',
  LaLiga: 'laliga.jpg',
  Bundesliga: 'bundesliga.jpg',
  'Serie A': 'serie-a.jpg',
  'Ligue 1': 'ligue-1.jpg',
  Eliteserien: 'eliteserien.jpg',
  'Botola Pro': 'botola.jpg',
  'Saudi Pro League': 'saudi-pro-league.jpg',
  'Saudi Professional League': 'saudi-pro-league.jpg',
};

/** For women's matches, whatever the competition. */
export const WOMEN_HERO = 'women.jpg';

/** For competitions without their own photo; empty: those stay plain. */
export const DEFAULT_HEROES = ['default-header.jpg', 'default-arms.jpg', 'default-sunset.jpg'];

const fold = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** "Arsenal Women", "Germany U17 (W)", "Women's Super League", "Liga F", "Frauen-Bundesliga" and the like. */
const WOMEN = /\bwomen|\(w\)|\bfemenin|\bf[ée]minine|\bfrauen|\bliga f\b|\bwsl\b|\bnwsl\b|\btoppserien\b|\bdamallsvenskan\b|\bd1 arkema\b/i;

export function isWomen(competition: string, teams: string[] = []): boolean {
  return [competition, ...teams].some((name) => WOMEN.test(name));
}

/** The same small number for the same text, to pick a default photo. */
function pick(text: string, count: number): number {
  let sum = 0;
  for (const char of fold(text)) sum = (sum * 31 + char.charCodeAt(0)) % 9973;
  return sum % count;
}

interface HeroOptions {
  files?: Record<string, string>;
  defaults?: string[];
  women?: string;
}

/** The photo for a match, as a path under the app's base, or null for a plain card. */
export function heroImage(
  competition: string,
  teams: string[] = [],
  { files = HERO_FILES, defaults = DEFAULT_HEROES, women = WOMEN_HERO }: HeroOptions = {},
): string | null {
  if (women && isWomen(competition, teams)) return `heroes/${women}`;
  const wanted = fold(competition);
  const own = Object.entries(files).find(([name]) => fold(name) === wanted)?.[1];
  const file = own ?? (defaults.length ? defaults[pick(competition, defaults.length)] : '');
  return file ? `heroes/${file}` : null;
}
